"use client"

import * as React from "react"
import Link from "next/link"
import { usePathname, useRouter } from "next/navigation"
import { useAuth } from "@/lib/use-auth"
import { cn, getAvatarInitials } from "@/lib/utils"
// SPEC-146 (Sprint 70, 2026-09-07) — F-001 Notificaciones In-App. ADR-043.
// NotificationBadge renders the unread count next to the "Notificaciones"
// sidebar item (mounted in countOnly mode — lightweight polling + WS).
// Sprint 135 (2026-09-22, Founder directive): also mount a `variant="dot"`
// instance on the "Cuenta" PARENT group label so the user sees a red dot
// when the group is COLLAPSED (without it, the count badge is hidden
// inside the collapsed Collapsible — WhatsApp-style visibility).
import { NotificationBadge } from "@/components/notification-badge"
// Sprint 135 (2026-09-22, Founder directive): DirectMessageBadge renders
// the unread direct-message count next to the "Mis Directos" sidebar item
// + a red dot on the "Mi Red" PARENT group label. Mirrors NotificationBadge
// (polls /api/direct-messages/unread/count every 30s + listens to the
// `direct-message:new` WS event for real-time updates).
import { DirectMessageBadge } from "@/components/direct-message-badge"
// PR #1438 (v124.2 — follow-up #A2): OrderChatBadge renders the unread
// order-chat count next to the "Mis Compras" sidebar item + a red dot on
// the "MARKETPLACE" PARENT group label. Mirrors DirectMessageBadge (polls
// /api/market/orders/chat/unread-count every 30s + listens to the
// `chat:message:new` WS event for real-time updates).
import { OrderChatBadge } from "@/components/order-chat-badge"
import Image from "next/image"
// SPEC-081 Fase 1 §6.5: fetch feature flags in the AppSidebar to gate the
// "Profileia Market" nav item on `market_credits_enabled`. The existing
// packages page uses the same `api.getFeatureFlags(signal)` pattern (commit
// 8 added the typed `cliente_mode_enabled` field to FeatureFlags; Task A
// added the typed `market_credits_enabled` field). The fetch is best-effort —
// on failure, the nav item is hidden (fail-closed for an additive feature;
// the backend independently rejects requests when the flag is off).
import { api } from "@/lib/api-client"

// shadcn/ui components
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarSeparator,
  useSidebar,
} from "@/components/ui/sidebar"
// AUDIT-018 fix (FASE 3 C1): eliminado SidebarRail de imports — ya no se usa.

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { Collapsible, CollapsibleTrigger, CollapsibleContent } from "@/components/ui/collapsible"

// Icons used by the AppSidebar footer dropdown menu + Collapsible trigger.
// The NAV_ITEMS icons (Home, User, Radio, etc.) live in `../nav-config.ts`
// (preserved VERBATIM with their original import-block comments).
import { ChevronDown, LogOut, User, Settings } from "lucide-react"

import { NAV_GROUPS, NAV_ITEMS } from "../nav-config"

// ---------------------------------------------------------------------------
// AppSidebar — the left sidebar with nav items
// ---------------------------------------------------------------------------

export function AppSidebar() {
  const pathname = usePathname()
  const router = useRouter()
  const { user, logout } = useAuth()
  const { state } = useSidebar()
  // SPEC-081 Fase 1 §6.5: fetch feature flags once on mount to gate the
  // "Profileia Market" nav item on `market_credits_enabled`. Default
  // `false` so the item is hidden until the fetch succeeds (fail-closed
  // for an additive feature — the backend independently rejects requests
  // when the flag is off, so this frontend gate is a UX affordance, not
  // the authoritative check).
  // SPEC-081 Fase 2 §6.11: same pattern for `market_sellers_enabled` +
  // the new "Portal Vendedor" nav item. Both flags are fetched in the
  // same `getFeatureFlags` call (no extra round-trip).
  // Anti-P-043 (CM-28): AbortController cancels the in-flight request
  // when the sidebar unmounts (e.g., user logs out → AppShell unmounts).
  const [marketCreditsEnabled, setMarketCreditsEnabled] = React.useState(false)
  // SPEC-081 Fase 2 §6.11: default `false` so the "Portal Vendedor" item
  // is hidden until the flag fetch succeeds (fail-closed for an additive
  // feature — the backend independently rejects requests when the flag
  // is off, so this frontend gate is a UX affordance, not the
  // authoritative check).
  const [marketSellersEnabled, setMarketSellersEnabled] = React.useState(false)
  // SPEC-081 Fase 3 §6.8: default `false` so the "Market" (catalog) item
  // is hidden until the flag fetch succeeds (fail-closed for an additive
  // feature — the backend independently rejects requests when the flag is
  // off, so this frontend gate is a UX affordance, not the authoritative
  // check). Independent from `market_credits_enabled` + `market_sellers_enabled`
  // so Founder can soft-launch product publication separately from credit
  // purchase + seller onboarding.
  const [marketProductsEnabled, setMarketProductsEnabled] = React.useState(false)
  // SPEC-AI-CONTEXT-FILE-STORAGE PR2 (2026-10-21): default `true` for the
  // "Almacenamiento" item — per SPEC §7.3 the flag is fail-open (visible
  // by default). The backend `/api/auth/features` endpoint does NOT yet
  // return `user_storage_enabled` (PR1 BE did not wire the env var; PR3
  // will). Until PR3, `flags.user_storage_enabled` is `undefined` — the
  // `!== false` check below treats `undefined` as `true` (visible) and
  // only hides the item when the backend explicitly returns `false`.
  // Anti-P-347: no invented logic — the fail-open default + the
  // `!== false` check trace to SPEC §7.3 + CR-CHECKLIST v2.4 §E1.
  const [userStorageEnabled, setUserStorageEnabled] = React.useState(true)
  React.useEffect(() => {
    const controller = new AbortController()
    api.getFeatureFlags(controller.signal)
      .then((flags) => {
        setMarketCreditsEnabled(flags.market_credits_enabled === true)
        setMarketSellersEnabled(flags.market_sellers_enabled === true)
        setMarketProductsEnabled(flags.market_products_enabled === true)
        // SPEC §7.3 — fail-open: visible unless the backend explicitly
        // returns `false`. The PR2 FE ships before PR3 BE wires the env
        // var, so `undefined` is the expected runtime value here.
        setUserStorageEnabled(flags.user_storage_enabled !== false)
      })
      .catch(() => {
        // Best-effort — keep the default `false` (items hidden). The
        // backend independently rejects requests when either flag is off.
        // SPEC-AI-CONTEXT-FILE-STORAGE PR2: keep `userStorageEnabled=true`
        // (fail-open per SPEC §7.3) on fetch failure — the storage page
        // is functional via the API even if the flag fetch fails.
      })
    return () => controller.abort()
  }, [])

  // Filter nav items by feature flag. Items without `featureFlag` are
  // always visible; items with `featureFlag` require the corresponding
  // flag to be `true`.
  const visibleNavItems = NAV_ITEMS.filter((item) => {
    if (!item.featureFlag) return true
    if (item.featureFlag === 'market_credits_enabled') {
      return marketCreditsEnabled
    }
    if (item.featureFlag === 'market_sellers_enabled') {
      return marketSellersEnabled
    }
    if (item.featureFlag === 'market_products_enabled') {
      return marketProductsEnabled
    }
    if (item.featureFlag === 'user_storage_enabled') {
      // SPEC-AI-CONTEXT-FILE-STORAGE §7.3 — fail-open (default true).
      return userStorageEnabled
    }
    return true
  })

  // P-109 (SPEC-090 §1.6 — session expired UX): when `user` is null
  // AFTER the initial load completes (session expired → useAuth set
  // user=null), the sidebar used to fall back to "Usuario" / "Gratuito"
  // / "U" — producing the misleading "U Usuario Networker · Gratuito"
  // state reported in P-109. Now we render an empty placeholder while
  // `user` is null so the brief pre-redirect window (between the
  // session-expired event firing + the `router.replace` completing)
  // does NOT display fake user data. The session-expired handler in
  // AppShell fires `router.replace('/login?reason=session_expired')`
  // immediately, so the user is moved off this UI within ms — but
  // even briefly showing "Usuario" is a UX bug.
  const displayName = user?.profile?.name || user?.username || ""
  const avatar = user?.profile?.avatar || undefined
  const packageName = user?.package?.name ?? 'gratuito'
  // SPEC-068 / ADR-037: roleType label for the sidebar widget — shown next
  // to the username (C-09 — "Badge discreto en perfil + propio dashboard").
  // Spec ref: SPEC-068 §4.2 (UX — sidebar widget shows Networker/Cliente),
  //           §3.7 (default 'networker' for legacy users without the field).
  // PR fix (Founder report 2026-11-01): pre-fix, users WITHOUT a roleType
  // (new users, no package activated) showed "Networker" — misleading
  // because they haven't chosen a role yet. Now: undefined/null → 'Usuario'.
  // Only show 'Networker' when roleType is explicitly 'networker', and
  // 'Cliente' when explicitly 'cliente'. 'Usuario' for everyone else.
  const roleLabel = user?.roleType === 'cliente' ? 'Cliente'
    : user?.roleType === 'networker' ? 'Networker'
    : 'Usuario'

  // P-UX-013 (SPEC-165): standardized avatar initials via shared helper.
  // Pre-fix used per-word initials ("John Doe" → "JD") with "U" fallback —
  // produced 3 distinct states across page loads ("q" / "QU" / "U").
  const initials = getAvatarInitials(displayName, user?.username ?? "")

  // H-04 fix: removed redundant router.replace('/login') — logout()
  // already redirects to /login?reason=logout, so the explicit
  // router.replace was overriding the ?reason query param.
  const onLogout = React.useCallback(async () => {
    await logout()
  }, [logout])

  return (
    <Sidebar collapsible="icon" className="border-r border-border/50">
      {/* ── Logo header ── */}
      <SidebarHeader className="px-3 py-4">
        <Link
          href="/app"
          className={cn(
            "flex items-center gap-2 transition-all duration-200",
            state === "collapsed" && "justify-center"
          )}
        >
          {/* Isotipo icon — always visible */}
          <div className="relative flex size-8 shrink-0 items-center justify-center overflow-hidden rounded-lg bg-gradient-to-br from-[#2563EB] to-[#8B5CF6]">
{/* PO-08 — Next.js <Image> for logo optimization (local static asset) */}
{/* P-UX-025 (Sprint 84 QA — 2026-09-10): alt="" porque la
                imagen es decorativa — el wordmark adyacente (<span>Profileia</span>
                en la línea inferior) ya provee el nombre del sitio. Con alt="Profileia"
                los screen readers anunciaban "Profileia Profileia Sistema Digital"
                (duplicación). WCAG 2.1 SC 1.1.1 — cuando el texto adyacente ya
                provee el nombre, la imagen es decorativa y debe tener alt="". */}
            <Image
              src="/logo/isotipo-white-512x512.png"
              alt=""
              width={20}
              height={20}
              className="size-5 object-contain"
            />
          </div>
          {/* Wordmark — hidden when collapsed */}
          <div
            className={cn(
              "flex flex-col overflow-hidden transition-all duration-200",
              state === "collapsed" && "max-w-0 opacity-0"
            )}
          >
            <span className="text-sm font-bold tracking-tight text-foreground">
              Profileia
            </span>
            <span className="text-[10px] font-medium leading-none text-muted-foreground">
              Sistema Digital
            </span>
          </div>
        </Link>
      </SidebarHeader>

      <SidebarSeparator />

      {/* ── Navigation ── */}
      <SidebarContent>
        {NAV_GROUPS.map((group) => {
          const groupItems = visibleNavItems.filter((item) => item.group === group.id)
          if (groupItems.length === 0) return null
          // Founder directive 2026-09-19: grupos desplegables para compactar.
          // El grupo "inicio" (1 item) siempre expandido — no vale la pena colapsarlo.
          const isAlwaysOpen = group.id === 'inicio'
          const hasActiveItem = groupItems.some((item) =>
            item.href === "/app" ? pathname === "/app" : pathname.startsWith(item.href)
          )
          return (
            <SidebarGroup key={group.id}>
              {state !== "collapsed" && !isAlwaysOpen && (
                <Collapsible defaultOpen={hasActiveItem}>
                  <CollapsibleTrigger className="flex w-full items-center justify-between px-3 py-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground/70 hover:text-muted-foreground transition-colors">
                    {/* Sprint 135 (2026-09-22): wrap the label + the optional
                        dot in a flex span so the dot sits inline next to the
                        uppercase label text. The ChevronDown stays on the
                        right edge via the parent `justify-between`. Only the
                        "cuenta" + "mi-red" groups get a dot — the other
                        groups ("dinero", "marketplace", "aprendizaje") have
                        no unread state to surface. */}
                    <span className="flex items-center gap-1.5">
                      {group.label}
                      {group.id === 'cuenta' && <NotificationBadge variant="dot" />}
                      {group.id === 'mi-red' && <DirectMessageBadge variant="dot" />}
                      {group.id === 'marketplace' && <OrderChatBadge variant="dot" />}
                    </span>
                    <ChevronDown className="size-3 transition-transform [[data-state=open]>&]:rotate-180" />
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    <SidebarGroupContent>
                      <SidebarMenu>
                        {groupItems.map((item) => {
                          const isActive =
                            item.href === "/app"
                              ? pathname === "/app"
                              : pathname.startsWith(item.href)
                          return (
                            <SidebarMenuItem key={item.href}>
                              <SidebarMenuButton
                                asChild
                                isActive={isActive}
                                tooltip={item.title}
                                className={cn(
                                  "group flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors duration-150",
                                  isActive
                                    ? "bg-[#2563EB]/10 text-[#2563EB] dark:bg-[#2563EB]/20 dark:text-[#60A5FA]"
                                    : "text-muted-foreground hover:bg-accent hover:text-accent-foreground"
                                )}
                              >
                                <Link href={item.href} className="flex items-center gap-3">
                                  <item.icon
                                    className={cn(
                                      "size-4 shrink-0 transition-colors",
                                      isActive
                                        ? "text-[#2563EB] dark:text-[#60A5FA]"
                                        : "text-muted-foreground group-hover:text-sidebar-accent-foreground"
                                    )}
                                  />
                                  <span>{item.title}</span>
                                  {item.href === '/app/notifications' && <NotificationBadge />}
                                  {/* Sprint 135 (2026-09-22): unread DM count
                                       on "Mis Directos" (WhatsApp-style). */}
                                  {item.href === '/app/network/directos' && <DirectMessageBadge />}
                                  {/* PR #1438 (v124.2): unread order-chat count
                                       on "Mis Compras" (WhatsApp-style —
                                       mirror "Mis Directos" badge). */}
                                  {item.href === '/market/ordenes' && <OrderChatBadge />}
                                </Link>
                              </SidebarMenuButton>
                            </SidebarMenuItem>
                          )
                        })}
                      </SidebarMenu>
                    </SidebarGroupContent>
                  </CollapsibleContent>
                </Collapsible>
              )}
              {state !== "collapsed" && isAlwaysOpen && (
                <>
                  <SidebarGroupLabel className="text-xs font-semibold uppercase tracking-wider text-muted-foreground/70 px-3 py-1">
                    {group.label}
                  </SidebarGroupLabel>
                  <SidebarGroupContent>
                    <SidebarMenu>
                      {groupItems.map((item) => {
                        const isActive =
                          item.href === "/app"
                            ? pathname === "/app"
                            : pathname.startsWith(item.href)
                        return (
                          <SidebarMenuItem key={item.href}>
                            <SidebarMenuButton
                              asChild
                              isActive={isActive}
                              tooltip={item.title}
                              className={cn(
                                "group flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors duration-150",
                                isActive
                                  ? "bg-[#2563EB]/10 text-[#2563EB] dark:bg-[#2563EB]/20 dark:text-[#60A5FA]"
                                  : "text-muted-foreground hover:bg-accent hover:text-accent-foreground"
                              )}
                            >
                              <Link href={item.href} className="flex items-center gap-3">
                                <item.icon
                                  className={cn(
                                    "size-4 shrink-0 transition-colors",
                                    isActive
                                      ? "text-[#2563EB] dark:text-[#60A5FA]"
                                      : "text-muted-foreground group-hover:text-sidebar-accent-foreground"
                                  )}
                                />
                                <span>{item.title}</span>
                                {item.href === '/app/notifications' && <NotificationBadge />}
                              </Link>
                            </SidebarMenuButton>
                          </SidebarMenuItem>
                        )
                      })}
                    </SidebarMenu>
                  </SidebarGroupContent>
                </>
              )}
              {/* Collapsed mode — all items visible (icons only, no group labels) */}
              {state === "collapsed" && (
                <SidebarGroupContent>
                  <SidebarMenu>
                    {groupItems.map((item) => {
                      const isActive =
                        item.href === "/app"
                          ? pathname === "/app"
                          : pathname.startsWith(item.href)
                      return (
                        <SidebarMenuItem key={item.href}>
                          <SidebarMenuButton
                            asChild
                            isActive={isActive}
                            tooltip={item.title}
                            className={cn(
                              "group flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors duration-150",
                              isActive
                                ? "bg-[#2563EB]/10 text-[#2563EB] dark:bg-[#2563EB]/20 dark:text-[#60A5FA]"
                                : "text-muted-foreground hover:bg-accent hover:text-accent-foreground"
                            )}
                          >
                            <Link href={item.href} className="flex items-center gap-3">
                              <item.icon
                                className={cn(
                                  "size-4 shrink-0 transition-colors",
                                  isActive
                                    ? "text-[#2563EB] dark:text-[#60A5FA]"
                                    : "text-muted-foreground group-hover:text-sidebar-accent-foreground"
                                )}
                              />
                              <span>{item.title}</span>
                              {item.href === '/app/notifications' && <NotificationBadge />}
                              {/* Sprint 135 (2026-09-22): unread DM count on
                                   "Mis Directos" — also shown in collapsed
                                   mode (icon-only) so the badge appears next
                                   to the icon, WhatsApp-style. */}
                              {item.href === '/app/network/directos' && <DirectMessageBadge />}
                              {/* PR #1438 (v124.2): unread order-chat count on
                                   "Mis Compras" — also shown in collapsed
                                   mode (icon-only) so the badge appears next
                                   to the icon, WhatsApp-style. */}
                              {item.href === '/market/ordenes' && <OrderChatBadge />}
                            </Link>
                          </SidebarMenuButton>
                        </SidebarMenuItem>
                      )
                    })}
                  </SidebarMenu>
                </SidebarGroupContent>
              )}
            </SidebarGroup>
          )
        })}
      </SidebarContent>

      <SidebarSeparator />

      {/* ── Footer: user info ── */}
      <SidebarFooter>
        <SidebarMenu>
          <SidebarMenuItem>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <SidebarMenuButton
                  size="lg"
                  className={cn(
                    "flex items-center gap-3 rounded-lg px-3 py-2",
                    state === "collapsed" && "justify-center px-0"
                  )}
                >
                  <Avatar className="size-8 shrink-0 border border-border/50">
                    <AvatarImage src={avatar} alt={displayName} />
                    <AvatarFallback className="bg-[#2563EB]/10 text-xs font-semibold text-[#2563EB] dark:bg-[#2563EB]/20 dark:text-[#60A5FA]">
                      {initials}
                    </AvatarFallback>
                  </Avatar>
                  <div
                    className={cn(
                      "flex flex-col overflow-hidden transition-all duration-200",
                      state === "collapsed" && "max-w-0 opacity-0"
                    )}
                  >
                    <span className="truncate text-sm font-medium text-foreground">
                      {displayName}
                    </span>
                    <span className="truncate text-xs text-muted-foreground">
{/* / : show "Networker" or "Cliente"
                          next to the package name in the sidebar widget
                          (C-09 — Badge discreto en propio dashboard).
Spec ref: (UX — sidebar widget),
(default 'networker' for legacy). */}
                      {roleLabel} · {packageName}
                    </span>
                  </div>
                  <ChevronDown
                    className={cn(
                      "ml-auto size-4 text-muted-foreground transition-all duration-200",
                      state === "collapsed" && "hidden"
                    )}
                  />
                </SidebarMenuButton>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                side="top"
                align="start"
                className="w-56"
              >
                <DropdownMenuItem asChild>
                  <Link href="/app/profile" className="cursor-pointer">
                    <User className="mr-2 size-4" />
                    Profileia Hub
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link href="/app/settings" className="cursor-pointer">
                    <Settings className="mr-2 size-4" />
                    Configuración
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  className="cursor-pointer text-destructive focus:text-destructive"
                  onClick={onLogout}
                >
                  <LogOut className="mr-2 size-4" />
                  Cerrar Sesión
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>

      {/* AUDIT-018 fix (FASE 3 C1): eliminado <SidebarRail /> — era un
          botón "Toggle Sidebar" pequeño visible en el borde izquierdo
          del sidebar en desktop. Es estándar de shadcn pero poco útil en
          desktop (el sidebar ya se puede colapsar via el SidebarTrigger
          del TopBar). En mobile el Sheet (drawer overlay) maneja el open/close.
          Si se quiere restaurar en el futuro, volver a añadir <SidebarRail />. */}
    </Sidebar>
  )
}
